import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Eye, EyeOff, Lock, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [requires2FASetup, setRequires2FASetup] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [twoFAMethods, setTwoFAMethods] = useState([]);
  const [activeMethod, setActiveMethod] = useState('authenticator');
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailCooldown, setEmailCooldown] = useState(0);
  const [adminRecipients, setAdminRecipients] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [warming, setWarming] = useState(true);
  const navigate = useNavigate();

  // Cold-start warmup: ping a lightweight endpoint on mount so the backend
  // is already booted by the time the user clicks "Login" after a fresh
  // deploy. Most modern serverless / containerised hosts cold-start in 60-90s.
  useEffect(() => {
    let cancelled = false;
    const ping = async (attempt = 1) => {
      try {
        await axios.get(`${API}/health`, { timeout: 90000 });
        if (!cancelled) setWarming(false);
      } catch (err) {
        if (cancelled) return;
        if (attempt < 3) {
          setTimeout(() => ping(attempt + 1), 5000);
        } else {
          setWarming(false);
        }
      }
    };
    ping();
    return () => { cancelled = true; };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email: email.trim(), password, totp_code: totpCode ? (activeMethod === 'email' ? `email_${totpCode}` : totpCode) : undefined },
        { withCredentials: true, timeout: 90000 }
      );

      // 2FA required
      if (data.requires_2fa) {
        setRequires2FA(true);
        setTwoFAMethods(data.methods || ['authenticator']);
        setActiveMethod('authenticator');
        toast.info('Enter your 2FA code');
        setLoading(false);
        return;
      }

      // 2FA setup required
      if (data.requires_2fa_setup) {
        localStorage.setItem('admin_token', data.temp_token);
        await initiate2FASetup(data.temp_token);
        setLoading(false);
        return;
      }

      // Successful login
      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_email', data.email);
      localStorage.setItem('admin_id', data.id);
      localStorage.setItem('admin_role', data.role);
      toast.success('Login successful!');
      onLogin();
      navigate('/overview');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again.');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Backend is still warming up after deploy — wait ~60s and try again.');
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        toast.error(`Cannot reach backend (${error.message || 'network error'}). Check API URL/CORS or contact admin.`);
      } else {
        toast.error('Login failed: ' + (error.message || 'unknown error'));
      }
    }
    setLoading(false);
  };

  const initiate2FASetup = async (token) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/2fa/setup`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 90000 }
      );
      setSetupData(data);
      setRequires2FASetup(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate 2FA setup');
    }
  };

  const fetchAdminRecipients = async () => {
    try {
      const { data } = await axios.post(
        `${API}/auth/2fa/admin-recipients`,
        { email: email.trim(), password }
      );
      setAdminRecipients(data.recipients || []);
      const self = (data.recipients || []).find(r => r.is_self);
      setSelectedRecipient(self ? self.id : '');
    } catch { /* ignore */ }
  };

  const handleSendEmailCode = async () => {
    setLoading(true);
    if (!selectedRecipient && adminRecipients.length > 0) {
      toast.error('Select who should receive the code');
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/auth/2fa/send-email`,
        { email: email.trim(), password, target_user_id: selectedRecipient || undefined },
        { withCredentials: true, timeout: 90000 }
      );
      setEmailCodeSent(true);
      setActiveMethod('email');
      setTotpCode('');
      toast.success(data.message || 'Code sent');
      setEmailCooldown(60);
      const interval = setInterval(() => setEmailCooldown(prev => { if (prev <= 1) { clearInterval(interval); return 0; } return prev - 1; }), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email code');
    }
    setLoading(false);
  };

  const handleVerify2FASetup = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/auth/2fa/verify-setup`,
        { code: verifyCode },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 90000 }
      );
      toast.success('2FA enabled! Please log in again.');
      setRequires2FASetup(false);
      setSetupData(null);
      setRequires2FA(false);
      setTotpCode('');
      setVerifyCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    }
    setLoading(false);
  };

  // 2FA Setup Screen
  if (requires2FASetup && setupData) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.loginBox, maxWidth: 520 }}>
          <div style={styles.header}>
            <Shield size={40} style={{ color: '#22c55e', marginBottom: 8 }} />
            <h1 style={{ ...styles.title, fontSize: '1.5rem' }}>Set Up Two-Factor Authentication</h1>
            <p style={{ ...styles.subtitle, marginTop: 8 }}>Required for security compliance (NIS2/ISO 27001)</p>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '0.85rem', color: '#475569', marginBottom: '1rem' }}>
              1. Install an authenticator app (Google Authenticator, Authy, or 1Password)
            </p>
            <p style={{ fontSize: '0.85rem', color: '#475569', marginBottom: '1rem' }}>
              2. Scan this QR code with your authenticator app:
            </p>
            <div style={{ textAlign: 'center', margin: '1.25rem 0', padding: '1rem', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
              <img src={`data:image/png;base64,${setupData.qr_code}`} alt="2FA QR Code" style={{ width: 180, height: 180 }} data-testid="2fa-qr-code" />
              <p style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 8, wordBreak: 'break-all' }}>Manual entry: {setupData.secret}</p>
            </div>
            <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.8rem', color: '#92400e', fontWeight: 600, margin: '0 0 4px' }}>Backup Codes (save these securely!):</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {setupData.backup_codes.map((code, i) => (
                  <code key={i} style={{ fontSize: '0.78rem', background: '#fff', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace' }}>{code}</code>
                ))}
              </div>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#475569' }}>
              3. Enter the 6-digit code from your app to verify:
            </p>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              data-testid="2fa-verify-input"
              type="text"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && verifyCode.length === 6 && handleVerify2FASetup()}
              style={{ ...styles.input, textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.3em', fontWeight: 700, flex: 1 }}
            />
            <button
              data-testid="2fa-verify-btn"
              onClick={handleVerify2FASetup}
              disabled={loading || verifyCode.length !== 6}
              style={{ padding: '0.75rem 1.5rem', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', opacity: verifyCode.length === 6 ? 1 : 0.5 }}
            >
              {loading ? 'Verifying...' : 'Verify & Enable'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.loginBox}>
        <div style={styles.header}>
          <Lock size={32} style={{ color: '#3b82f6', marginBottom: 8 }} />
          <h1 style={styles.title}>ProductHub</h1>
          <p style={styles.subtitle}>by Cloudvoro — Secure Portal</p>
        </div>

        <form onSubmit={handleLogin} style={styles.form}>
          {!requires2FA ? (
            <>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Email</label>
                <div style={{ position: 'relative' }}>
                  <Mail size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#94a3b8' }} />
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    required data-testid="login-email-input"
                    style={{ ...styles.input, paddingLeft: 36 }}
                    placeholder="you@company.com"
                  />
                </div>
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#94a3b8' }} />
                  <input
                    type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                    required data-testid="login-password-input"
                    style={{ ...styles.input, paddingLeft: 36, paddingRight: 40 }}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div style={styles.inputGroup}>
              {/* Method toggle tabs */}
              {twoFAMethods.includes('email') && (
                <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderRadius: 8, overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
                  <button type="button" onClick={() => { setActiveMethod('authenticator'); setTotpCode(''); }}
                    style={{ flex: 1, padding: '0.55rem', border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                      background: activeMethod === 'authenticator' ? '#0f172a' : '#f8fafc', color: activeMethod === 'authenticator' ? '#fff' : '#64748b' }}>
                    <Shield size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Authenticator App
                  </button>
                  <button type="button" onClick={() => { setActiveMethod('email'); setTotpCode(''); fetchAdminRecipients(); }}
                    data-testid="email-2fa-tab"
                    style={{ flex: 1, padding: '0.55rem', border: 'none', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                      background: activeMethod === 'email' ? '#0f172a' : '#f8fafc', color: activeMethod === 'email' ? '#fff' : '#64748b' }}>
                    <Mail size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Email Code
                  </button>
                </div>
              )}

              {activeMethod === 'authenticator' ? (
                <>
                  <label style={styles.label}>Authenticator Code</label>
                  <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 8 }}>Enter the 6-digit code from your authenticator app, or a backup code</p>
                </>
              ) : (
                <>
                  <label style={styles.label}>Email Verification Code</label>
                  {!emailCodeSent ? (
                    <div>
                      <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 10 }}>Choose who should receive the code:</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                        {adminRecipients.map(r => (
                          <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.5rem 0.75rem', borderRadius: 8, border: selectedRecipient === r.id ? '2px solid #3b82f6' : '1.5px solid #e2e8f0', background: selectedRecipient === r.id ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '0.88rem' }}>
                            <input type="radio" name="recipient" value={r.id} checked={selectedRecipient === r.id} onChange={() => setSelectedRecipient(r.id)} style={{ accentColor: '#3b82f6' }} />
                            <span style={{ fontWeight: 600, color: '#0f172a' }}>{r.display_name}</span>
                          </label>
                        ))}
                        {adminRecipients.length === 0 && <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Loading recipients...</p>}
                      </div>
                      <button type="button" onClick={handleSendEmailCode} disabled={loading || !selectedRecipient}
                        data-testid="send-email-code-btn"
                        style={{ width: '100%', padding: '0.7rem', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', opacity: selectedRecipient ? 1 : 0.5 }}>
                        {loading ? 'Sending...' : 'Send Code'}
                      </button>
                    </div>
                  ) : (
                    <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: 8 }}>
                      Code sent. Enter the 6-digit code below.
                    </p>
                  )}
                </>
              )}

              {(activeMethod === 'authenticator' || emailCodeSent) && (
                <div style={{ position: 'relative' }}>
                  {activeMethod === 'authenticator'
                    ? <Shield size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#22c55e' }} />
                    : <Mail size={16} style={{ position: 'absolute', left: 12, top: 12, color: '#3b82f6' }} />
                  }
                  <input
                    type="text" value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 8))}
                    required data-testid="login-2fa-input" autoFocus
                    style={{ ...styles.input, paddingLeft: 36, textAlign: 'center', fontSize: '1.3rem', letterSpacing: '0.2em', fontWeight: 600 }}
                    placeholder="000000"
                  />
                </div>
              )}

              {activeMethod === 'email' && emailCodeSent && (
                <button type="button" onClick={handleSendEmailCode} disabled={emailCooldown > 0 || loading}
                  data-testid="resend-email-code-btn"
                  style={{ marginTop: 6, background: 'none', border: 'none', color: emailCooldown > 0 ? '#94a3b8' : '#3b82f6', cursor: emailCooldown > 0 ? 'default' : 'pointer', fontSize: '0.82rem' }}>
                  {emailCooldown > 0 ? `Resend code in ${emailCooldown}s` : 'Resend code'}
                </button>
              )}

              <button type="button" onClick={() => { setRequires2FA(false); setTotpCode(''); setActiveMethod('authenticator'); setEmailCodeSent(false); }}
                style={{ marginTop: 6, background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: '0.82rem' }}>
                Back to login
              </button>
            </div>
          )}

          {(!requires2FA || activeMethod === 'authenticator' || emailCodeSent) && (
            <button type="submit" disabled={loading} data-testid="login-submit-button"
              style={{ width: '100%', marginTop: '1rem', padding: '0.85rem', background: '#1e293b', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: '0.95rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Authenticating...' : requires2FA ? 'Verify & Login' : 'Sign In'}
            </button>
          )}
        </form>

        <div style={{ marginTop: '1.5rem', padding: '0.75rem', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
          <p style={{ fontSize: '0.72rem', color: '#166534', textAlign: 'center', margin: 0 }}>
            Protected by 2FA, AES-256 encryption, and session management. NIS2/ISO 27001 compliant.
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#0f172a', padding: '2rem',
  },
  loginBox: {
    background: 'white', borderRadius: '16px', padding: '2.5rem',
    width: '100%', maxWidth: '440px', boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
  },
  header: { textAlign: 'center', marginBottom: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  title: { fontSize: '1.75rem', fontWeight: '700', color: '#0f172a', marginBottom: '0.25rem' },
  subtitle: { color: '#64748b', fontSize: '0.9rem' },
  form: { display: 'flex', flexDirection: 'column', gap: '1.1rem' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { fontSize: '0.82rem', fontWeight: '600', color: '#475569' },
  input: { padding: '0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box', width: '100%' },
};
